import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { formStyles } from '../utils/formStyles';
import { selectStyles } from '../utils/selectStyles';
import { FileUploadButton } from '../components/FileUploadButton';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SeriesQuickCreate } from './quickCreate/SeriesQuickCreate';
import { ColorQuickCreate } from './quickCreate/ColorQuickCreate';
import { AwardQuickCreate } from './quickCreate/AwardQuickCreate';
import { ExhibitionQuickCreate } from './quickCreate/ExhibitionQuickCreate';
import { processFiles } from '../../../utils/heicConverter';
import { useTranslation } from 'react-i18next';
import { createData, updateData, fetchData } from '../utils/apiClient';
import { useIsDarkMode } from '../utils/useIsDarkMode';
import { useAdmin } from '../context/AdminContext';

const API_URL = import.meta.env.VITE_API_URL;

export function PaintingForm({ 
  formData = { 
    name: '',
    year: '',
    size: '',
    price: '',
    serie_id: '',
    color_ids: [], 
    award_ids: [], 
    exhibition_ids: [],
    description_de: '',
    description_en: '',
    images: [],
    main_image_idx: 0,
    sold: false,
    featured: false
  }, 
  handleChange, 
  handleImageChange, 
  handleMainImageIdx,
  handleSubmit, 
  series = [], 
  colors = [], 
  awards = [], 
  exhibitions = [],
  isEdit = false,
  onSeriesUpdate,
  onAwardsUpdate,
  onCancel,
  onUpdate
}) {
  const { t } = useTranslation();
  const isDark = useIsDarkMode();
  const { handleCreate, fetchData: fetchAdminData } = useAdmin();
  const [quickCreateData, setQuickCreateData] = useState({
    color: { name_de: '', name_en: '', color_code: '#000000' },
    award: { name: '', date: '', location: '' },
    exhibition: { name: '', locations: '', start_date: '', end_date: '', description: '' },
    series: { name: '', description: '' }
  });
  const [showQuickCreate, setShowQuickCreate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);

  // Defensive defaults for array fields
  const safeFormData = {
    ...formData,
    color_ids: Array.isArray(formData.color_ids) ? formData.color_ids : [],
    award_ids: Array.isArray(formData.award_ids) ? formData.award_ids : [],
    exhibition_ids: Array.isArray(formData.exhibition_ids) ? formData.exhibition_ids : [],
    images: Array.isArray(formData.images) ? formData.images : [],
  };

  const getColorById = (id) => colors.find(c => c.id === Number(id));

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };

  // Helper function to format exhibition labels with dates
  const formatExhibitionLabel = (exhibition) => {
    const startDate = exhibition.start_date ? formatDate(exhibition.start_date) : null;
    const endDate = exhibition.end_date ? formatDate(exhibition.end_date) : null;
    
    if (startDate && endDate) {
      return `${exhibition.name} (${startDate} - ${endDate})`;
    } else if (startDate) {
      return `${exhibition.name} (${startDate})`;
    }
    return exhibition.name;
  };

  // Helper function to format award labels with dates
  const formatAwardLabel = (award) => {
    return award.date ? `${award.name} (${formatDate(award.date)})` : award.name;
  };

  const handleQuickCreate = async (type, dataOverride) => {
    try {
      // Map the type to the correct data key
      const dataKey = type === 'exhibitions' ? 'exhibition' : (type === 'colors' ? 'color' : type);
      const data = dataOverride || quickCreateData[dataKey];
      console.log('Creating with data:', { type, dataKey, data });
      // Ensure we have the correct data structure
      const newItem = await handleCreate(type, data);
      console.log('Created new item:', newItem);
      // Update form data based on type
      if (type === 'exhibitions') {
        handleChange({
          target: {
            name: 'exhibition_ids',
            value: [...safeFormData.exhibition_ids, newItem.id]
          }
        });
        await fetchAdminData('exhibitions');
      } else if (type === 'series') {
        handleChange({
          target: {
            name: 'serie_id',
            value: newItem.id
          }
        });
        if (onSeriesUpdate) {
          await onSeriesUpdate(newItem);
        }
      } else if (type === 'awards') {
        handleChange({
          target: {
            name: 'award_ids',
            value: [...safeFormData.award_ids, newItem.id]
          }
        });
        if (onAwardsUpdate) {
          await onAwardsUpdate(newItem);
        }
      } else if (type === 'colors') {
        handleChange({
          target: {
            name: 'color_ids',
            value: [...safeFormData.color_ids, newItem.id]
          }
        });
      }
      // Reset quick create form
      setQuickCreateData(prev => ({
        ...prev,
        [dataKey]: type === 'colors' ? { name_de: '', name_en: '', color_code: '#000000' } :
                  type === 'exhibitions' ? { name: '', locations: '', start_date: '', end_date: '', description: '' } :
                  { name: '', description: '' }
      }));
      setShowQuickCreate(null);
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
      alert(`Error creating ${type}: ${error.message}`);
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    // Check if any HEIC files are present
    const heicFiles = files.filter(file => 
      file.name.toLowerCase().endsWith('.heic') || 
      file.name.toLowerCase().endsWith('.heif')
    );
    
    if (heicFiles.length > 0) {
      setIsProcessingFiles(true);
      console.log(`Processing ${heicFiles.length} HEIC file(s) for painting...`);
    }
    
    try {
      // Process files (convert HEIC to JPEG if needed)
      const processedFiles = await processFiles(files, 0.9); // 90% quality
      
      const newImages = [...safeFormData.images, ...processedFiles.map(file => ({
        file,
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        preview: URL.createObjectURL(file)
      }))];
      
      handleChange({
        target: {
          name: 'images',
          value: newImages
        }
      });
      
      if (heicFiles.length > 0) {
        console.log(`Successfully converted ${heicFiles.length} HEIC file(s) to JPEG for painting`);
      }
    } catch (error) {
      console.error('Error processing painting images:', error);
      // Fallback: use original files if conversion fails
      const newImages = [...safeFormData.images, ...files.map(file => ({
        file,
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        preview: URL.createObjectURL(file)
      }))];
      handleChange({
        target: {
          name: 'images',
          value: newImages
        }
      });
    } finally {
      setIsProcessingFiles(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = [...safeFormData.images];
    // Revoke the object URL to avoid memory leaks
    if (newImages[index].preview) {
      URL.revokeObjectURL(newImages[index].preview);
    }
    newImages.splice(index, 1);
    handleChange({
      target: {
        name: 'images',
        value: newImages
      }
    });
    // If we removed the main image, reset main_image_idx
    if (index === safeFormData.main_image_idx) {
      handleChange({
        target: {
          name: 'main_image_idx',
          value: 0
        }
      });
    }
  };

  const handleSetMainImage = (index) => {
    handleChange({
      target: {
        name: 'main_image_idx',
        value: index
      }
    });
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare submission data
      const submissionData = {
        ...safeFormData,
        // Ensure arrays are properly initialized
        color_ids: safeFormData.color_ids || [],
        award_ids: safeFormData.award_ids || [],
        exhibition_ids: safeFormData.exhibition_ids || [],
        // Ensure main_image_idx is set
        main_image_idx: safeFormData.main_image_idx || 0
      };

      console.log('Submitting painting data:', submissionData);
      console.log('Images in submission:', submissionData.images);
      console.log('Number of new images:', submissionData.images.filter(img => img.file instanceof File).length);

      // Call the parent's handleSubmit function
      await handleSubmit(event);

      // Cleanup any temporary image previews
      safeFormData.images.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });

      // Reset form state
      setIsSubmitting(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      safeFormData.images.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  // Helper for robust image preview
  function getImageSrc(image) {
    if (image.preview && typeof image.preview === 'string') {
      // Neu hochgeladenes Bild (lokale Vorschau)
      return image.preview;
    } else if (typeof image === 'string') {
      // Fallback: String (selten)
      return image.startsWith('http') ? image : `${API_URL}${image}`;
    } else if (image.file && typeof image.file === 'string') {
      // Bereits gespeichertes Bild (Backend-URL)
      return image.file.startsWith('http') ? image.file : `${API_URL}${image.file}`;
    } else if (image.file && image.file instanceof File) {
      try {
        return URL.createObjectURL(image.file);
      } catch (e) {
        return '';
      }
    } else if (image.thumb_path) {
      // Verwende Thumbnail wenn verfügbar
      return image.thumb_path.startsWith('http') ? image.thumb_path : `${API_URL}${image.thumb_path}`;
    } else if (image.file_path) {
      // Fallback auf file_path wenn kein Thumbnail verfügbar
      return image.file_path.startsWith('http') ? image.file_path : `${API_URL}${image.file_path}`;
    }
    return '';
  }

  // Set correct main_image_idx when editing
  useEffect(() => {
    if (isEdit && safeFormData.images && safeFormData.images.length > 0) {
      let idx = 0;
      // Prefer main_image_id if present
      if (safeFormData.main_image_id) {
        idx = safeFormData.images.findIndex(img => img.id === safeFormData.main_image_id || img.file === safeFormData.main_image_id || img.file_path === safeFormData.main_image_id);
      } else {
        // Fallback: look for is_main flag
        idx = safeFormData.images.findIndex(img => img.is_main);
      }
      if (idx < 0) idx = 0;
      if (safeFormData.main_image_idx !== idx) {
        handleChange({ target: { name: 'main_image_idx', value: idx } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, safeFormData.images, safeFormData.main_image_id]);

  return (
    <form onSubmit={handleFormSubmit} className="space-y-8 w-full relative">
      {/* Loading Overlay */}
      {(isSubmitting || showSuccess || isProcessingFiles) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-zinc-800 rounded-xl p-8 shadow-xl flex flex-col items-center gap-4">
            {isProcessingFiles ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  {t('paintingform.processing_files', 'HEIC-Dateien werden konvertiert...')}
                </p>
              </>
            ) : isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  {t('paintingform.saving')}
                </p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  {t('paintingform.saved')}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="mb-2">
        <h2 className="text-2xl font-bold mb-1 text-gray-900 dark:text-white">
          {isEdit ? t('paintingform.section_title_edit') : t('paintingform.section_title')}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {t('paintingform.section_desc')}
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
        {/* Basic Information */}
        <div className="lg:col-span-2 bg-white dark:bg-zinc-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-zinc-700 pb-2">
            {t('paintingform.basic_info')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <input
                type="text"
                id="name"
                name="name"
                value={safeFormData.name}
                onChange={handleChange}
                className={formStyles.input}
                required
              />
              <label htmlFor="name" className={`${formStyles.label} ${safeFormData.name ? formStyles.labelActive : ''}`}>
                {t('paintingform.name')}
              </label>
            </div>
            <div className="relative">
              <input
                type="number"
                id="year"
                name="year"
                value={safeFormData.year}
                onChange={handleChange}
                min="1900"
                max={new Date().getFullYear()}
                className={formStyles.input}
                required
              />
              <label htmlFor="year" className={`${formStyles.label} ${safeFormData.year ? formStyles.labelActive : ''}`}>
                {t('paintingform.year')}
              </label>
            </div>
            <div className="relative">
              <input
                type="text"
                id="size"
                name="size"
                value={safeFormData.size}
                onChange={handleChange}
                placeholder="e.g., 100x100 cm"
                className={formStyles.input}
                required
              />
              <label htmlFor="size" className={`${formStyles.label} ${safeFormData.size ? formStyles.labelActive : ''}`}>
                {t('paintingform.size')}
              </label>
            </div>
            <div className="relative">
              <input
                type="number"
                id="price"
                name="price"
                value={safeFormData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                className={formStyles.input}
                required
              />
              <label htmlFor="price" className={`${formStyles.label} ${safeFormData.price ? formStyles.labelActive : ''}`}>
                {t('paintingform.price')}
              </label>
            </div>
            {/* Sold Checkbox */}
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id="sold"
                name="sold"
                checked={!!safeFormData.sold}
                onChange={e => handleChange({ target: { name: 'sold', value: e.target.checked } })}
                className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-2"
              />
              <label htmlFor="sold" className="text-sm font-medium text-gray-700 dark:text-gray-200 select-none cursor-pointer">
                {t('paintingform.sold')}
              </label>
            </div>
            {/* Featured Checkbox mit Stern-Icon */}
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={!!safeFormData.featured}
                onChange={e => handleChange({ target: { name: 'featured', value: e.target.checked } })}
                className="h-5 w-5 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded mr-2"
              />
              <label htmlFor="featured" className="text-sm font-medium text-gray-700 dark:text-gray-200 select-none cursor-pointer flex items-center gap-1">
                {/* Stern-Icon gefüllt/umrissen je nach Wert */}
                {safeFormData.featured ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5 text-yellow-500"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5 text-yellow-500"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.518 4.674a1 1 0 00.95.69h4.905c.969 0 1.371 1.24.588 1.81l-3.974 2.89a1 1 0 00-.364 1.118l1.518 4.674c.3.921-.755 1.688-1.54 1.118l-3.974-2.89a1 1 0 00-1.176 0l-3.974 2.89c-.784.57-1.838-.197-1.54-1.118l1.518-4.674a1 1 0 00-.364-1.118l-3.974-2.89c-.783-.57-.38-1.81.588-1.81h4.905a1 1 0 00.95-.69l1.518-4.674z"/></svg>
                )}
                {t('paintingform.featured', 'Ausgewähltes Werk')}
              </label>
            </div>
          </div>
        </div>

        {/* Relationships */}
        <div className="lg:col-span-2 bg-white dark:bg-zinc-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-zinc-700 pb-2">
            {t('paintingform.relationships')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Serie */}
            <div className="relative">
              <div className="text-xs font-semibold mb-1 text-gray-700 dark:text-gray-200">
                {t('paintingform.series')}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  id="serie_id"
                  name="serie_id"
                  value={series.find(s => s.id === Number(safeFormData.serie_id)) ? {
                    value: Number(safeFormData.serie_id),
                    label: series.find(s => s.id === Number(safeFormData.serie_id))?.name
                  } : null}
                  onChange={(option) => handleChange({ target: { name: 'serie_id', value: option?.value || '' } })}
                  options={series.map(s => ({ value: s.id, label: s.name }))}
                  className="w-full"
                  styles={selectStyles(isDark)}
                  isClearable
                />
                <SeriesQuickCreate
                  quickCreateData={quickCreateData}
                  setQuickCreateData={setQuickCreateData}
                  handleQuickCreate={handleQuickCreate}
                />
              </div>
            </div>
            {/* Farben */}
            <div className="relative">
              <div className="text-xs font-semibold mb-1 text-gray-700 dark:text-gray-200">
                {t('paintingform.colors')}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  id="color_ids"
                  name="color_ids"
                  value={safeFormData.color_ids.map(id => {
                    const color = colors.find(c => c.id === Number(id));
                    return color ? {
                      value: color.id,
                      label: `${color.name_de} / ${color.name_en}`,
                      color: color.color_code
                    } : null;
                  }).filter(Boolean)}
                  onChange={(options) => handleChange({ 
                    target: { 
                      name: 'color_ids', 
                      value: options ? options.map(o => o.value) : [] 
                    } 
                  })}
                  options={colors.map(c => ({ 
                    value: c.id, 
                    label: `${c.name_de} / ${c.name_en}`,
                    color: c.color_code
                  }))}
                  className="w-full"
                  styles={selectStyles(isDark)}
                  isMulti
                  isClearable
                  formatOptionLabel={option => (
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full border border-gray-300 dark:border-zinc-600" 
                        style={{ backgroundColor: option.color }}
                      />
                      <span>{option.label}</span>
                    </div>
                  )}
                />
                <ColorQuickCreate
                  quickCreateData={quickCreateData}
                  setQuickCreateData={setQuickCreateData}
                  handleQuickCreate={handleQuickCreate}
                />
              </div>
            </div>
            {/* Awards */}
            <div className="relative">
              <div className="text-xs font-semibold mb-1 text-gray-700 dark:text-gray-200">
                {t('paintingform.awards')}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  id="award_ids"
                  name="award_ids"
                  value={safeFormData.award_ids.map(id => {
                    const award = awards.find(a => a.id === Number(id));
                    return award ? { value: award.id, label: formatAwardLabel(award) } : null;
                  }).filter(Boolean)}
                  onChange={(options) => handleChange({ 
                    target: { 
                      name: 'award_ids', 
                      value: options ? options.map(o => o.value) : [] 
                    } 
                  })}
                  options={awards.map(a => ({ value: a.id, label: formatAwardLabel(a) }))}
                  className="w-full"
                  styles={selectStyles(isDark)}
                  isMulti
                  isClearable
                />
                <AwardQuickCreate
                  quickCreateData={quickCreateData}
                  setQuickCreateData={setQuickCreateData}
                  handleQuickCreate={handleQuickCreate}
                />
              </div>
            </div>
            {/* Ausstellungen */}
            <div className="relative">
              <div className="text-xs font-semibold mb-1 text-gray-700 dark:text-gray-200">
                {t('paintingform.exhibitions')}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  id="exhibition_ids"
                  name="exhibition_ids"
                  value={safeFormData.exhibition_ids
                    .map(id => {
                      const e = exhibitions.find(e => e.id === Number(id));
                      return e ? { value: e.id, label: formatExhibitionLabel(e) } : null;
                    })
                    .filter(Boolean)
                  }
                  onChange={options =>
                    handleChange({
                      target: {
                        name: 'exhibition_ids',
                        value: options ? options.map(o => o.value) : []
                      }
                    })
                  }
                  options={exhibitions.map(e => ({ value: e.id, label: formatExhibitionLabel(e) }))}
                  className="w-full"
                  styles={selectStyles(isDark)}
                  isMulti
                  isClearable
                />
                <ExhibitionQuickCreate
                  quickCreateData={quickCreateData}
                  setQuickCreateData={setQuickCreateData}
                  handleQuickCreate={handleQuickCreate}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Descriptions */}
        <div className="lg:col-span-2 bg-white dark:bg-zinc-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-zinc-700 pb-2">
            {t('paintingform.descriptions')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <textarea
                id="description_de"
                name="description_de"
                value={safeFormData.description_de}
                onChange={handleChange}
                className={`${formStyles.input} min-h-[100px]`}
                required
              />
              <label htmlFor="description_de" className={`${formStyles.label} ${safeFormData.description_de ? formStyles.labelActive : ''}`}>
                {t('paintingform.description_de')}
              </label>
            </div>
            <div className="relative">
              <textarea
                id="description_en"
                name="description_en"
                value={safeFormData.description_en}
                onChange={handleChange}
                className={`${formStyles.input} min-h-[100px]`}
                required
              />
              <label htmlFor="description_en" className={`${formStyles.label} ${safeFormData.description_en ? formStyles.labelActive : ''}`}>
                {t('paintingform.description_en')}
              </label>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="lg:col-span-2 bg-white dark:bg-zinc-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-zinc-700 pb-2">
            {t('paintingform.images')}
          </h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <input
                type="file"
                id="images"
                onChange={handleImageUpload}
                multiple
                accept="image/*,.heic,.heif"
                className="hidden"
              />
              <label
                htmlFor="images"
                className={`${formStyles.fileUploadButton} cursor-pointer inline-block`}
              >
                {t('paintingform.upload_images')}
              </label>
              {safeFormData.images.length > 0 && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {safeFormData.images.length} {t('paintingform.images_selected', 'Bilder ausgewählt')}
                </span>
              )}
            </div>
            
            {safeFormData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {safeFormData.images.map((image, index) => {
                  const src = getImageSrc(image);
                  return (
                    <div key={image.id || `image-${index}`} className="relative group aspect-square">
                      {src && (
                        <img
                          src={src}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                          onError={e => { e.target.style.background = '#eee'; e.target.src = '/fallback.png'; }}
                        />
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex flex-col items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSetMainImage(index)}
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            safeFormData.main_image_idx === index
                              ? 'bg-green-500 text-white'
                              : 'bg-white text-gray-800 hover:bg-gray-100'
                          }`}
                        >
                          {safeFormData.main_image_idx === index
                            ? t('paintingform.main_image')
                            : t('paintingform.set_main')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="px-3 py-1 rounded-full text-sm font-medium bg-red-500 text-white hover:bg-red-600"
                        >
                          {t('paintingform.remove_image', 'Entfernen')}
                        </button>
                      </div>
                      {safeFormData.main_image_idx === index && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                          {t('paintingform.main_image')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button 
          type="submit" 
          disabled={isSubmitting || isProcessingFiles}
          className={`${formStyles.button} ${(isSubmitting || isProcessingFiles) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isProcessingFiles
            ? t('paintingform.processing', 'Verarbeitung...')
            : isSubmitting
            ? t('paintingform.saving', 'Speichern...')
            : isEdit 
            ? t('paintingform.update') 
            : t('paintingform.create')
          }
        </button>
        {isEdit && onCancel && (
          <button 
            type="button" 
            onClick={onCancel} 
            className="ml-4 px-4 py-2 bg-gray-300 dark:bg-zinc-700 rounded text-gray-800 dark:text-gray-200"
          >
            {t('paintingform.cancel')}
          </button>
        )}
      </div>
    </form>
  );
} 